// inferTimersFromInstruction — runtime fallback for AI-generated /
// imported recipes whose steps describe a duration in prose but
// don't carry a structured `step.timer` / `step.timers` field.
//
// Why: the generate-recipe edge function doesn't (currently) prompt
// Claude to emit timer fields, and parse-recipe-text occasionally
// misses durations buried in prose ("sauté for 5 minutes ... cook
// 1 more minute"). Without a fallback, these steps render with no
// countdown UI even though the cook just read "5 minutes" off the
// screen.
//
// This function does pure pattern matching on the instruction
// string. It returns either a `timer` (single integer seconds) or a
// `timers` array of `{label, seconds}` stages — same shape CookMode
// already consumes via the single Timer / TimerStack components.
// The output is a UI-only fill; nothing gets written back to the
// recipe row, so re-renders stay stable and the LLM's output is
// preserved as-is on disk.
//
// Anti-goal: don't be clever. We're not parsing "until softened"
// (cue, not duration) or "overnight" (not a stove timer). We pick
// up the obvious "<number> <unit>" patterns and stop. False
// positives are worse than no fallback — a wrong 1-minute timer
// would wrongly chime mid-stir.

const NUMBER = String.raw`(\d+(?:\.\d+)?)`;
const RANGE  = String.raw`(?:\s*(?:[-–—]|to|or)\s*(\d+(?:\.\d+)?))?`;
const UNIT   = String.raw`(minutes?|mins?|seconds?|secs?|hours?|hrs?)`;
// Optional adjective between number and unit: "1 MORE minute", "5
// ADDITIONAL minutes", "another 30 seconds". Bounded to one specific
// word so a generic `\w+` doesn't grab phrases like "1 cup of olive
// oil" → match "1 ...minutes". Words match how cooks actually write.
const FILLER = String.raw`(?:\s+(?:more|additional|extra|final|further|whole|full|total|short|quick|long|good))?`;
// Trailing context required: a unit + a word boundary OR end of
// pattern. Lookbehind blocks "last 2 minutes" from the END of a
// previous timer match — kept simple here, the dedup pass below
// handles overlapping ranges.

// Skip a match when the IMMEDIATELY preceding word marks the number
// as a frequency, not a duration. Anchored to end-of-lead so words
// from earlier in the sentence don't false-positive — "Sear 3 minutes
// per side, then rest 10 minutes" must NOT skip the 10 just because
// "per" appears before "side". The lookbehind on `lead` (the slice
// up to the match start) catches only "every|each|per ..." sitting
// right next to the number.
//
//   "stir every 30 seconds"     — skip
//   "rest 10 minutes"           — KEEP
//   "after 10 minutes"          — KEEP
//   "for 5 minutes"             — KEEP
const SKIP_PRECEDING = /\b(every|each|per|interval)\s*$/i;

const REGEX = new RegExp(
  `${NUMBER}${RANGE}${FILLER}\\s*${UNIT}\\b`,
  "gi",
);

function unitToSeconds(rawUnit, n) {
  const u = String(rawUnit).toLowerCase();
  if (u.startsWith("hour") || u.startsWith("hr")) return Math.round(n * 3600);
  if (u.startsWith("min")) return Math.round(n * 60);
  if (u.startsWith("sec")) return Math.round(n);
  return null;
}

// Build a label by walking back from the match for the nearest verb-
// like token ("sauté", "cook", "simmer", "rest", "bake", "boil", "fry",
// "sear", "roast", "steam", "reduce", "stir", "knead", "rise", "proof").
// Falls back to "Cook" so the TimerStack accordion still has a label.
const LABEL_VERBS = [
  "sauté", "saute", "cook", "simmer", "rest", "bake", "boil",
  "fry", "sear", "roast", "steam", "reduce", "stir", "knead",
  "rise", "proof", "marinate", "chill", "soak", "infuse",
  "braise", "broil", "toast", "warm", "heat",
];
function labelFor(text, matchStart) {
  const window = text.slice(Math.max(0, matchStart - 80), matchStart).toLowerCase();
  for (let i = LABEL_VERBS.length - 1; i >= 0; i -= 1) {
    const v = LABEL_VERBS[i];
    if (window.endsWith(`${v} `) || window.endsWith(`${v} for `)
        || window.includes(`${v} for `) || window.includes(` ${v} `)
        || window.includes(`${v}é for `)) {
      return v.charAt(0).toUpperCase() + v.slice(1);
    }
  }
  return "Cook";
}

/**
 * Extract clockable timers from a step instruction string.
 *
 * @param {string} instruction
 * @returns {{ timer?: number, timers?: Array<{label: string, seconds: number}> } | null}
 *   - `timer` (seconds) when there's exactly one duration cue
 *   - `timers` array when there are 2+ distinct cues (becomes a TimerStack)
 *   - `null` when no clockable cue was found
 */
export function inferTimersFromInstruction(instruction) {
  if (!instruction || typeof instruction !== "string") return null;
  const text = instruction;
  const found = [];
  let match;
  // .matchAll would be cleaner, but Safari < 13.1 doesn't support
  // it on RegExp instances. The exec+lastIndex loop is universal.
  REGEX.lastIndex = 0;
  while ((match = REGEX.exec(text)) !== null) {
    const start = match.index;
    // Skip if "every"/"each"/etc. precedes within ~24 chars — that's
    // a frequency cue, not a wait.
    const lead = text.slice(Math.max(0, start - 24), start);
    if (SKIP_PRECEDING.test(lead)) continue;
    const lo = parseFloat(match[1]);
    const hi = match[2] ? parseFloat(match[2]) : null;
    const unit = match[3];
    if (!Number.isFinite(lo)) continue;
    // For ranges ("5-7 minutes"), pick the LOW end. Ringing early
    // is recoverable (the cook can extend); ringing late means the
    // food is overdone. Same convention CookMode already uses for
    // observed-duration calibration falling back to the nominal.
    const n = hi != null && Number.isFinite(hi) && hi >= lo ? lo : lo;
    const seconds = unitToSeconds(unit, n);
    if (!seconds || seconds < 5) continue;       // < 5s is noise
    if (seconds > 6 * 3600) continue;            // > 6h isn't a stove timer
    found.push({ seconds, start, label: labelFor(text, start) });
  }
  if (found.length === 0) return null;

  // De-dupe by start position window: regex can occasionally emit
  // overlapping matches when ranges are present. Two matches whose
  // start positions are within 2 chars are the same cue.
  const deduped = [];
  for (const f of found) {
    const last = deduped[deduped.length - 1];
    if (last && f.start - last.start < 2) continue;
    deduped.push(f);
  }
  if (deduped.length === 1) {
    return { timer: deduped[0].seconds };
  }
  return {
    timers: deduped.map((d, i) => ({
      label: d.label || `Stage ${i + 1}`,
      seconds: d.seconds,
    })),
  };
}

/**
 * Resolve the timers a step should actually surface, reconciling the
 * stored fields with the prose. ONE source of truth so every consumer
 * (the active-step render, the Live Activity rail, telemetry
 * pre-registration) agrees on what timers a step has.
 *
 * Priority:
 *   1. Explicit `step.timers[]` — the author/AI deliberately split
 *      stages; always honored verbatim.
 *   2. Otherwise, if the prose carries 2+ distinct duration cues,
 *      prefer the inferred per-cue SPLIT — even when a single
 *      `step.timer` is also set. This fixes the "three 30-second
 *      mixes collapsed into one 1:00 timer" bug: generation sometimes
 *      rolls multiple cues into a single `step.timer`, hiding the
 *      per-action structure the cook reads in the prose. The prose is
 *      the source of truth the cook follows, so one timer per cue.
 *   3. Otherwise the single `step.timer` (or a single inferred cue).
 *
 * @param {{timer?: number, timers?: Array, instruction?: string}} step
 * @returns {{ timer?: number, timers?: Array<{label,seconds}> } | null}
 */
export function resolveStepTimers(step) {
  if (!step || typeof step !== "object") return null;
  const explicit = Array.isArray(step.timers) && step.timers.length > 0
    ? step.timers
    : null;
  if (explicit) return { timers: explicit };

  const inferred = inferTimersFromInstruction(step.instruction || "");
  // 2+ prose cues beat a rolled-up single timer.
  if (inferred?.timers && inferred.timers.length > 1) {
    return { timers: inferred.timers };
  }
  const single = (Number(step.timer) > 0 ? Number(step.timer) : null)
    || (inferred?.timer || null);
  return single ? { timer: single } : null;
}
